import { supabase } from '../lib/supabase/client';
import { Job, InsertJob, UpdateJob } from '../types/supabase.types';

interface JobResponse {
  data: Job[] | null;
  error: Error | null;
}

interface SingleJobResponse {
  data: Job | null;
  error: Error | null;
}

export const jobService = {
  // Get all jobs with optional filters
  async getAll(filters?: {
    search?: string;
    experience?: string;
    jobType?: string;
    location?: string;
  }): Promise<JobResponse> {
    try {
      let query = supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.search) {
        query = query.or(`job_title.ilike.%${filters.search}%,company.ilike.%${filters.search}%,location.ilike.%${filters.search}%`);
      }

      if (filters?.experience && filters.experience !== 'all') {
        query = query.ilike('experience_level', `%${filters.experience}%`);
      }

      if (filters?.jobType && filters.jobType !== 'all') {
        query = query.eq('job_type', filters.jobType);
      }

      if (filters?.location && filters.location !== 'all') {
        query = query.ilike('location', `%${filters.location}%`);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get job by ID
  async getById(id: string): Promise<SingleJobResponse> {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Create new job
  async create(job: InsertJob): Promise<SingleJobResponse> {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .insert(job)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Update job
  async update(id: string, updates: UpdateJob): Promise<SingleJobResponse> {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Delete job
  async delete(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },
};